import { useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Press } from '@dorixona/ui'
import { Icon } from '../../ui/Icon'
import { useCurrentLocation } from '../../app/location'
import { useMapsOn } from '../../app/settings'
import type { Store } from './api'
import { StoresMap } from './StoresMap'
import { RouteLink } from './RouteLink'
import { distanceMeters, distanceText, hoursText } from './model'

export interface MapRow {
  store: Store
  /** Подпись строки: имя аптеки так, как его назвал ответ о наличии. */
  name: string
  /** Правая колонка строки: цена и остаток, сумма за всё — что важно экрану. */
  side: ReactNode
}

export interface StoresOnMapProps {
  rows: MapRow[]
}

/**
 * Аптеки на карте и списком под ней — в одном порядке: номер на булавке —
 * номер строки. Сначала ничего не выбрано: видно все аптеки сразу. Нажали
 * булавку или строку — под картой её часы и схема проезда, строка
 * подсвечена; крестик снимает выбор. Геолокацию сами не спрашиваем: «Где я» —
 * по кнопке, а с точкой видно расстояние.
 *
 * Общая часть экранов «Аптеки на карте» товара и «Где есть всё» мультипоиска.
 */
export function StoresOnMap({ rows }: StoresOnMapProps) {
  const { t, i18n } = useTranslation(['catalog', 'checkout', 'stores', 'common'])
  const here = useCurrentLocation(false)
  const [selected, setSelected] = useState(0)
  const [meFocus, setMeFocus] = useState(0)
  const [locating, setLocating] = useState(false)
  const [locateFailed, setLocateFailed] = useState(false)
  const current = rows.find(({ store }) => store.id === selected)
  const mapsOn = useMapsOn()

  const distance = (store: Store) =>
    here.data && store.lat !== null && store.lng !== null ? (
      <span className="co-dist num">
        {distanceText(
          distanceMeters(here.data, { lat: store.lat, lng: store.lng }),
          t,
          i18n.language,
        )}
      </span>
    ) : null

  /** «Где я»: спрашиваем точку заново — доступ могли дать только что. */
  function locate() {
    setLocating(true)
    setLocateFailed(false)
    void here.refetch().then((result) => {
      setLocating(false)
      if (result.data) setMeFocus((n) => n + 1)
      else setLocateFailed(true)
    })
  }

  return (
    <>
      {mapsOn ? (
        <div className="addr-map-box">
          <div className="st-map ph-map">
            <StoresMap
              stores={rows.map(({ store }) => store)}
              selected={current ? current.store.id : null}
              onSelect={setSelected}
              me={here.data ?? null}
              meFocus={meFocus}
            />
          </div>
          <Press
            squish="icon"
            className="addr-locate"
            aria-label={t('checkout:pharmacyPage.locate')}
            disabled={locating}
            aria-busy={locating || undefined}
            onPress={locate}
          >
            <Icon name="i-locate" size={20} />
          </Press>
        </div>
      ) : null}
      {/* Карты выключены в админке — «Где я» строкой: расстояние до аптек
          считается и без карты. */}
      {mapsOn === false ? (
        <div className="card addr-switch">
          <Press
            squish="row"
            className="opt addr-row"
            disabled={locating}
            aria-busy={locating || undefined}
            onPress={locate}
          >
            <Icon name="i-locate" size={18} />
            <div className="t1">{t('checkout:pharmacyPage.locate')}</div>
          </Press>
        </div>
      ) : null}
      {locateFailed ? (
        <p className="acc-note acc-note-err" role="status">
          {t('checkout:pharmacyPage.locateFailed')}
        </p>
      ) : null}

      {current ? (
        <div className="card ph-chosen pm-chosen" aria-live="polite">
          <div className="opt">
            <Icon name="i-pin" size={18} />
            <div>
              <div className="t1">
                {current.name}
                {distance(current.store)}
              </div>
              <div className="t2">{current.store.address}</div>
              <div className="t2">
                {t('checkout:hours', { hours: hoursText(current.store, t) })}
              </div>
            </div>
            <Press
              squish="icon"
              className="pm-close"
              aria-label={t('common:actions.close')}
              onPress={() => {
                setSelected(0)
              }}
            >
              <Icon name="i-x" size={16} />
            </Press>
          </div>
          <RouteLink
            store={current.store}
            className="btn pm-route"
            label={t('stores:routeLabel', { name: current.name })}
          >
            <Icon name="i-pin" size={18} />
            {t('stores:route')}
          </RouteLink>
        </div>
      ) : null}

      <div className="card pm-list">
        {rows.map(({ store, name, side }, index) => (
          <Press
            key={store.id}
            squish="row"
            className={store.id === selected ? 'pm-row on' : 'pm-row'}
            aria-pressed={store.id === selected}
            onPress={() => {
              setSelected(store.id)
              // Из глубины списка — к карте: там выбранная аптека и маршрут.
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
          >
            <span className="pm-num num">{index + 1}</span>
            <span className="pm-main">
              <span className="t1">
                {name}
                {distance(store)}
              </span>
              <span className="t2">{store.address}</span>
              <span className={store.openNow ? 'pm-open' : 'pm-open off'}>
                {t(store.openNow ? 'checkout:open' : 'checkout:closed')}
              </span>
            </span>
            <span className="pm-side">{side}</span>
          </Press>
        ))}
      </div>
    </>
  )
}
